(() => {
  const MESSAGE_SELECTOR = '.pb-paypal-message-under-checkout [data-pp-message]';
  const READY_CLASS = 'pb-paypal-message--ready';
  const CART_MUTATION_URL = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[?#]|$)/i;

  const existingState = window.pbPayPalCartState;

  if (existingState?.initialized) {
    existingState.captureSdk?.();
    existingState.scheduleRender?.(0);
    existingState.queueCartSync?.();
    return;
  }

  const state = existingState || {};
  const nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  state.initialized = true;
  state.sdk = state.sdk || null;
  state.renderTimer = null;
  state.syncTimer = null;
  state.syncRequest = 0;
  state.renderSequence = 0;
  window.pbPayPalCartState = state;

  const normaliseAmount = (value) => {
    const amount = Number.parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(amount) ? amount.toFixed(2) : '';
  };

  const messageRoot = (message) => message.closest('.pb-paypal-message-under-checkout');

  const hasRenderedPayPalMessage = (message) =>
    Boolean(message.querySelector('iframe')) &&
    (message.scrollHeight > 0 || message.offsetParent === null);

  const showFallback = (message) => {
    const root = messageRoot(message);
    if (!root) return;

    root.classList.remove(READY_CLASS);
  };

  const showPayPalMessage = (message) => {
    const root = messageRoot(message);
    if (!root) return;

    root.classList.add(READY_CLASS);
  };

  const captureSdk = () => {
    const sdk = window.pbPayPalSDK || window.PayPalSDK || window.paypal || state.sdk;

    if (sdk && typeof sdk.Messages === 'function') {
      state.sdk = sdk;
      window.pbPayPalSDK = sdk;
      return sdk;
    }

    return null;
  };

  const resetMessage = (message) => {
    showFallback(message);
    message.replaceChildren();
    delete message.dataset.pbPaypalRendered;
    delete message.dataset.pbPaypalRenderedAmount;
    delete message.dataset.pbPaypalRenderToken;
    message.dataset.pbPaypalRenderState = 'pending';
    message.removeAttribute('data-pp-id');
  };

  const confirmRendered = (message, token, attempt = 0) => {
    if (!message.isConnected || message.dataset.pbPaypalRenderToken !== token) return;

    if (hasRenderedPayPalMessage(message)) {
      message.dataset.pbPaypalRendered = 'true';
      message.dataset.pbPaypalRenderedAmount = normaliseAmount(message.dataset.ppAmount);
      message.dataset.pbPaypalRenderState = 'rendered';
      showPayPalMessage(message);
      return;
    }

    if (attempt < 20) {
      window.setTimeout(() => confirmRendered(message, token, attempt + 1), 50);
      return;
    }

    message.dataset.pbPaypalRendered = 'false';
    message.dataset.pbPaypalRenderState = 'unavailable';
    showFallback(message);
  };

  const renderMessage = (message, sdk, index) => {
    const amount = normaliseAmount(message.dataset.ppAmount);
    const renderedAmount = normaliseAmount(message.dataset.pbPaypalRenderedAmount);
    const hasIframe = Boolean(message.querySelector('iframe'));
    const hasRenderedMessage = hasRenderedPayPalMessage(message);

    if (hasRenderedMessage && (!renderedAmount || renderedAmount === amount)) {
      message.dataset.pbPaypalRendered = 'true';
      message.dataset.pbPaypalRenderedAmount = amount;
      message.dataset.pbPaypalRenderState = 'rendered';
      showPayPalMessage(message);
      return;
    }

    if (
      hasIframe ||
      message.childElementCount > 0 ||
      (renderedAmount && renderedAmount !== amount)
    ) {
      resetMessage(message);
    }

    if (
      message.dataset.pbPaypalRenderState === 'rendering' &&
      normaliseAmount(message.dataset.pbPaypalRenderedAmount) === amount
    ) {
      return;
    }

    const id =
      message.dataset.pbPaypalId ||
      `pb-paypal-cart-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`;
    const token = String(++state.renderSequence);

    message.dataset.pbPaypalId = id;
    message.dataset.pbPaypalRenderedAmount = amount;
    message.dataset.pbPaypalRenderToken = token;
    message.dataset.pbPaypalRenderState = 'rendering';
    showFallback(message);

    try {
      const result = sdk.Messages().render(`[data-pb-paypal-id="${id}"]`);

      Promise.resolve(result)
        .then(() => confirmRendered(message, token))
        .catch(() => {
          if (!message.isConnected || message.dataset.pbPaypalRenderToken !== token) return;
          message.dataset.pbPaypalRendered = 'false';
          message.dataset.pbPaypalRenderState = 'failed';
          showFallback(message);
        });
    } catch (_error) {
      message.dataset.pbPaypalRendered = 'false';
      message.dataset.pbPaypalRenderState = 'failed';
      showFallback(message);
    }
  };

  const renderMessages = () => {
    const messages = [...document.querySelectorAll(MESSAGE_SELECTOR)];

    messages.forEach((message) => {
      if (hasRenderedPayPalMessage(message)) {
        showPayPalMessage(message);
      } else {
        showFallback(message);
      }
    });

    const sdk = captureSdk();
    if (!sdk) return false;

    messages.forEach((message, index) => renderMessage(message, sdk, index));
    return true;
  };

  const scheduleRender = (delay = 0) => {
    window.clearTimeout(state.renderTimer);
    state.renderTimer = window.setTimeout(renderMessages, delay);
  };

  const syncCartAmount = async () => {
    if (!nativeFetch) {
      scheduleRender(0);
      return;
    }

    const requestId = ++state.syncRequest;

    try {
      const response = await nativeFetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

      const cart = await response.json();
      if (requestId !== state.syncRequest) return;

      const amount = (Number(cart.total_price || 0) / 100).toFixed(2);

      document.querySelectorAll(MESSAGE_SELECTOR).forEach((message) => {
        const currentAmount = normaliseAmount(message.dataset.ppAmount);

        if (currentAmount === amount) return;

        message.dataset.ppAmount = amount;
        resetMessage(message);
      });
    } catch (_error) {
      // The Liquid amount is still a valid fallback if /cart.js is unavailable.
    }

    scheduleRender(0);
  };

  const queueCartSync = () => {
    window.clearTimeout(state.syncTimer);
    state.syncTimer = window.setTimeout(syncCartAmount, 40);

    [120, 350, 800, 1500].forEach((delay) => {
      window.setTimeout(syncCartAmount, delay);
    });
  };

  const isCartMutationRequest = (input, init = {}) => {
    const url = String(input?.url || input || '');
    const method = String(init.method || input?.method || 'GET').toUpperCase();

    return CART_MUTATION_URL.test(url) || (method !== 'GET' && /\/cart(?:\.js)?(?:[?#]|$)/i.test(url));
  };

  if (nativeFetch && !window.pbPayPalFetchWrapped) {
    window.pbPayPalFetchWrapped = true;
    const originalFetch = window.fetch;

    window.fetch = function pbPayPalFetch(input, init) {
      const result = originalFetch.apply(this, arguments);

      if (isCartMutationRequest(input, init)) {
        Promise.resolve(result).finally(queueCartSync);
      }

      return result;
    };
  }

  if (window.XMLHttpRequest && !window.pbPayPalXhrWrapped) {
    window.pbPayPalXhrWrapped = true;
    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function pbPayPalXhrOpen(method, url) {
      this.pbPayPalCartMutation = isCartMutationRequest(url, { method });
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function pbPayPalXhrSend() {
      if (this.pbPayPalCartMutation) {
        this.addEventListener('loadend', queueCartSync, { once: true });
      }

      return originalSend.apply(this, arguments);
    };
  }

  state.captureSdk = captureSdk;
  state.scheduleRender = scheduleRender;
  state.queueCartSync = queueCartSync;
  window.pbRenderPayPalCartMessages = renderMessages;
  window.pbSchedulePayPalCartMessages = scheduleRender;

  window.addEventListener('pb:paypal-sdk-ready', () => {
    captureSdk();
    scheduleRender(0);
  });
  window.addEventListener('pb:paypal-sdk-error', () => {
    document.querySelectorAll(MESSAGE_SELECTOR).forEach(showFallback);
  });
  window.addEventListener('load', queueCartSync);
  window.addEventListener('pageshow', queueCartSync);
  document.addEventListener('shopify:section:load', queueCartSync);
  document.addEventListener('cart:updated', queueCartSync);
  document.addEventListener('cart:update', queueCartSync);
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('#CartForm')) queueCartSync();
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('#CartForm')) queueCartSync();
  });

  new MutationObserver((records) => {
    const needsRender = records.some((record) => {
      if (record.type === 'attributes') return record.target.matches?.(MESSAGE_SELECTOR);

      return [...record.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(MESSAGE_SELECTOR) || node.querySelector?.(MESSAGE_SELECTOR))
      );
    });

    if (needsRender) scheduleRender(0);
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-pp-amount']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scheduleRender(0);
      queueCartSync();
    }, { once: true });
  } else {
    scheduleRender(0);
    queueCartSync();
  }
})();
